import { motion } from "framer-motion";
import { ArrowLeft, Sparkles } from "lucide-react";
import { OPTIONS } from "@/lib/onboardingOptions";
import { cn } from "@/lib/utils";
import ChipGrid from "./ChipGrid";

const transition = { duration: 0.5, ease: [0.22, 1, 0.36, 1] };

export default function Step6OperatingStyle({ draft, setDraft, finish, prev, submitting }) {
  const set = (f) => (v) => setDraft((d) => ({ ...d, [f]: v }));
  const canFinish =
    draft.operating_mode && draft.aggressiveness && draft.biggest_bottleneck && draft.help_needed;

  return (
    <motion.div
      data-testid="onboarding-step-6"
      key="step-6"
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -12 }}
      transition={transition}
      className="text-white"
    >
      <h2 className="text-[28px] font-extrabold leading-[1.1] tracking-tight md:text-[32px]">
        How should your boardroom operate?
      </h2>
      <p className="mt-2 text-[13px] text-white/55">
        This tunes how aggressive every debate will be.
      </p>

      <div className="mt-6 space-y-6">
        <Group label="How do you want to operate?">
          <div
            data-testid="q-operating-mode-grid"
            className="grid grid-cols-1 gap-2 sm:grid-cols-3"
          >
            {OPTIONS.operating_mode.map((o) => {
              const active = draft.operating_mode === o.value;
              return (
                <button
                  key={o.value}
                  type="button"
                  data-testid={`q-operating-mode-${o.value}`}
                  data-selected={active ? "true" : "false"}
                  onClick={() => set("operating_mode")(o.value)}
                  className={cn(
                    "rounded-2xl border px-4 py-4 text-left transition-all",
                    active
                      ? "border-white bg-white text-black shadow-[0_10px_28px_-10px_rgba(255,255,255,0.5)]"
                      : "border-white/12 bg-white/5 text-white/80 hover:border-white/30 hover:bg-white/10",
                  )}
                >
                  <div className="text-[13px] font-extrabold">{o.label}</div>
                  <div className={cn("mt-1 text-[11px]", active ? "text-black/60" : "text-white/50")}>
                    {o.tagline}
                  </div>
                </button>
              );
            })}
          </div>
        </Group>

        <Group label="How aggressive should your AI board operate?">
          <ChipGrid
            testIdPrefix="q-aggressiveness"
            options={OPTIONS.aggressiveness}
            value={draft.aggressiveness}
            onChange={set("aggressiveness")}
            columns={2}
          />
        </Group>

        <Group label="What is your biggest bottleneck right now?">
          <ChipGrid
            testIdPrefix="q-biggest-bottleneck"
            options={OPTIONS.biggest_bottleneck}
            value={draft.biggest_bottleneck}
            onChange={set("biggest_bottleneck")}
            columns={3}
          />
        </Group>

        <Group label="What do you need help with most right now?">
          <ChipGrid
            testIdPrefix="q-help-needed"
            options={OPTIONS.help_needed}
            value={draft.help_needed}
            onChange={set("help_needed")}
            columns={3}
          />
        </Group>
      </div>

      <footer className="mt-8 flex items-center justify-between">
        <button
          type="button"
          data-testid="onboarding-step-back"
          onClick={prev}
          className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-white/60 hover:text-white"
        >
          <ArrowLeft className="h-3.5 w-3.5" strokeWidth={2.4} />
          Back
        </button>
        <button
          type="button"
          data-testid="onboarding-finish"
          disabled={!canFinish || submitting}
          onClick={finish}
          className="inline-flex items-center gap-2 rounded-full bg-white px-5 py-2.5 text-[13px] font-bold text-black shadow-[0_10px_30px_-10px_rgba(255,255,255,0.5)] transition-opacity disabled:opacity-40"
        >
          <Sparkles className="h-3.5 w-3.5" strokeWidth={2.6} />
          {submitting ? "Saving…" : "Enter boardroom →"}
        </button>
      </footer>
    </motion.div>
  );
}

function Group({ label, children }) {
  return (
    <div>
      <div className="eyebrow mb-2 text-white/55">{label}</div>
      {children}
    </div>
  );
}
